import { useLanguage, SUPPORTED_LANGUAGES } from "@/contexts/LanguageContext";
import { X } from "lucide-react";
import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";

const BANNER_MESSAGES: Record<string, { detected: string; switchBack: string }> = {
  en: { detected: "We set your language to", switchBack: "Switch to" },
  sv: { detected: "Vi ställde in ditt språk till", switchBack: "Byt till" },
  no: { detected: "Vi satte språket ditt til", switchBack: "Bytt til" },
  da: { detected: "Vi indstillede dit sprog til", switchBack: "Skift til" },
  fi: { detected: "Kieleksesi asetettiin", switchBack: "Vaihda:" },
  de: { detected: "Wir haben Ihre Sprache auf", switchBack: "Wechseln zu" },
  nl: { detected: "We hebben uw taal ingesteld op", switchBack: "Schakel naar" },
  fr: { detected: "Nous avons défini votre langue sur", switchBack: "Passer à" },
  es: { detected: "Hemos establecido tu idioma a", switchBack: "Cambiar a" },
  it: { detected: "Abbiamo impostato la tua lingua su", switchBack: "Cambia a" },
};

export function GeoLanguageBanner() {
  const { geoOverride, acceptGeoOverride, revertGeoOverride, language } = useLanguage();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (geoOverride) {
      const t = setTimeout(() => setVisible(true), 400);
      return () => clearTimeout(t);
    }
    setVisible(false);
  }, [geoOverride]);

  if (!geoOverride) return null;

  const msgs = BANNER_MESSAGES[language] ?? BANNER_MESSAGES.en;
  const detectedLabel = SUPPORTED_LANGUAGES.find((l) => l.code === geoOverride.detectedLang)?.label ?? geoOverride.detectedLang;
  const browserLabel = SUPPORTED_LANGUAGES.find((l) => l.code === geoOverride.browserLang)?.label ?? geoOverride.browserLang;

  return (
    <div
      className={cn(
        "bg-muted/80 backdrop-blur-sm border-b border-border transition-all duration-500 overflow-hidden",
        visible ? "max-h-16 opacity-100" : "max-h-0 opacity-0"
      )}
    >
      <div className="mx-auto max-w-6xl flex items-center justify-between gap-3 px-4 py-2 text-sm">
        <p className="text-muted-foreground min-w-0">
          {msgs.detected}{" "}
          <span className="font-medium text-foreground">{detectedLabel}</span>
          <span className="mx-1.5 text-border">·</span>
          <button
            onClick={revertGeoOverride}
            className="font-medium text-primary hover:underline underline-offset-2"
          >
            {msgs.switchBack} {browserLabel}
          </button>
        </p>
        <button
          onClick={acceptGeoOverride}
          className="shrink-0 rounded-full p-1 text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
          aria-label="Dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
